import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import FeedbackThread from './FeedbackThread';
import { amAdmin, myEmail } from '../lib/taskAccess';
import { managesFeedbackPeople } from '../lib/feedbackPeople.mjs';

const DISPOSITION_COLORS = {
  new_item: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  duplicate: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  already_shipped: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  quick_fix: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  needs_triage: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  needs_discussion: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const PRIORITY_COLORS = {
  low: 'text-gray-400',
  medium: 'text-blue-500',
  high: 'text-orange-500',
  critical: 'text-red-600 font-semibold',
};

const STATUS_OPTIONS = ['pending_intake', 'intake_active', 'finalized', 'accepted', 'rejected', 'shipped'];

/**
 * FeedbackTriage — admin inbox showing all feedback with AI analysis.
 * The route is not role-guarded: a non-admin sees only their own items from the API's list
 * and no status or admin-notes controls, which the API refuses from a non-admin.
 * The thread's add and remove people controls show only to the item's owner or an admin, as the API allows.
 */
export default function FeedbackTriage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});
  const [saving, setSaving] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDisposition, setFilterDisposition] = useState('');
  const admin = amAdmin();
  const email = myEmail();

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      const data = await api.get('/feedback/?' + params.toString());
      setItems(data.feedback || []);
    } catch {} finally { setLoading(false); }
  }

  async function updateStatus(id, status) {
    try {
      await api.put(`/feedback/${id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch {}
  }

  async function saveNotes(id) {
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await api.put(`/feedback/${id}`, { admin_notes: adminNotes[id] || '' });
    } catch {} finally { setSaving((s) => ({ ...s, [id]: false })); }
  }

  const dispositions = [...new Set(items.map((i) => i.disposition).filter(Boolean))];
  const filtered = items.filter((i) => {
    if (filterStatus && i.status !== filterStatus) return false;
    if (filterDisposition && i.disposition !== filterDisposition) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Feedback Triage</h2>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select
          value={filterDisposition}
          onChange={(e) => setFilterDisposition(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm"
        >
          <option value="">All dispositions</option>
          {dispositions.map((d) => <option key={d} value={d}>{d.replace('_', ' ')}</option>)}
        </select>
        <button onClick={load} className="ml-auto text-sm text-primary hover:underline">Refresh</button>
      </div>

      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      {!loading && filtered.length === 0 && <p className="text-sm text-gray-400">No feedback items found.</p>}

      {filtered.map((item) => {
        const analysis = item.ai_analysis_json || {};
        const ensemble = item.ai_ensemble_json || {};
        const isOpen = expanded === item.id;

        return (
          <div key={item.id} className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
            <div
              className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800"
              onClick={() => setExpanded(isOpen ? null : item.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate">{item.title || '(untitled)'}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${DISPOSITION_COLORS[item.disposition] || 'bg-gray-100 text-gray-700'}`}>
                    {(item.disposition || 'unknown').replace('_', ' ')}
                  </span>
                  <span className={`text-xs ${PRIORITY_COLORS[analysis.suggested_priority || item.suggested_priority] || 'text-gray-400'}`}>
                    {analysis.suggested_priority || item.suggested_priority || '–'} priority
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span>{item.module} / {item.category}</span>
                  <span>{item.user_email || 'unknown'}</span>
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700">{item.status}</span>
                </div>
              </div>
              {isOpen ? <ChevronUp size={16} className="text-gray-400 shrink-0 mt-1" /> : <ChevronDown size={16} className="text-gray-400 shrink-0 mt-1" />}
            </div>

            {isOpen && (
              <div className="border-t border-gray-100 dark:border-slate-700 px-4 py-4 space-y-4">
                {item.description && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.description}</p>
                  </div>
                )}

                {analysis.summary && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">AI Summary</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{analysis.summary}</p>
                    {analysis.open_risks?.length > 0 && (
                      <ul className="mt-1 list-disc list-inside text-xs text-orange-600 dark:text-orange-400">
                        {analysis.open_risks.map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                {ensemble.models?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Ensemble ({ensemble.agreement?.answered}/{ensemble.agreement?.of} models agreed)
                    </p>
                    <div className="space-y-1">
                      {ensemble.models.filter((m) => m.recommendation).map((m, i) => (
                        <div key={i} className="text-xs bg-gray-50 dark:bg-slate-800 rounded p-2">
                          <span className="font-mono text-gray-400">{m.model}</span>: {m.recommendation?.recommendation}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {item.screenshot_url && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Screenshot</p>
                    <a href={item.screenshot_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      View screenshot <ExternalLink size={12} />
                    </a>
                  </div>
                )}

                {admin && (<>
                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                  <select
                    value={item.status}
                    onChange={(e) => updateStatus(item.id, e.target.value)}
                    className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Admin Notes</p>
                  <div className="flex gap-2">
                    <textarea
                      rows={2}
                      value={adminNotes[item.id] ?? (item.admin_notes || '')}
                      onChange={(e) => setAdminNotes((n) => ({ ...n, [item.id]: e.target.value }))}
                      className="flex-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <button
                      onClick={() => saveNotes(item.id)}
                      disabled={saving[item.id]}
                      className="px-3 py-1 text-sm rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      {saving[item.id] ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
                </>)}

                <div className="border-t border-gray-100 dark:border-slate-700 pt-3">
                  <FeedbackThread feedbackId={item.id} isAdmin={managesFeedbackPeople(item, { email, admin })} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
